import { useState } from 'react'
import { Dialog, DialogTitle, DialogBody, DialogActions } from '@/components/catalyst/dialog'
import Button from '@/components/ui/Button'
import FormInput from '@/components/ui/form/FormInput'
import { TrashIcon } from '@heroicons/react/24/outline'

interface FilterPreset {
  id: number
  name: string
  setting: Record<string, unknown>
}

interface FiltersPresetsModalProps {
  open: boolean
  onClose: () => void
  presets: FilterPreset[]
  csrfToken: string
  onSave: (name: string) => Promise<void>
  onLoad: (preset: FilterPreset) => void
  onDelete: (name: string) => Promise<void>
}

export default function FiltersPresetsModal({
  open,
  onClose,
  presets,
  csrfToken: _csrfToken,
  onSave,
  onLoad,
  onDelete,
}: FiltersPresetsModalProps) {
  const [presetName, setPresetName] = useState('')
  const [saving, setSaving] = useState(false)
  const [deletingName, setDeletingName] = useState<string | null>(null)

  const handleSave = async () => {
    const trimmed = presetName.trim()
    if (!trimmed) return

    setSaving(true)
    try {
      await onSave(trimmed)
      setPresetName('')
    } finally {
      setSaving(false)
    }
  }

  const handleLoad = (preset: FilterPreset) => {
    onLoad(preset)
    onClose()
  }

  const handleDelete = async (name: string) => {
    if (!confirm('Delete this preset?')) return

    setDeletingName(name)
    try {
      await onDelete(name)
    } finally {
      setDeletingName(null)
    }
  }

  return (
    <Dialog open={open} onClose={onClose} size="md">
      <DialogTitle>Filter Presets</DialogTitle>

      <DialogBody data-testid="filters-presets-modal">
        {/* Save section */}
        <div className="flex items-end gap-3">
          <div className="flex-1">
            <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
              Save current filters as preset
            </label>
            <FormInput
              data-testid="preset-name-input"
              value={presetName}
              onChange={(e) => setPresetName(e.target.value)}
              placeholder="Preset name..."
            />
          </div>
          <Button
            data-testid="preset-save-button"
            disabled={!presetName.trim() || saving}
            onClick={handleSave}
          >
            {saving ? 'Saving...' : 'Save Current'}
          </Button>
        </div>

        {/* Divider */}
        <div className="mt-6 border-t border-zinc-200 dark:border-zinc-700" />

        {/* Existing presets list */}
        <div className="mt-4">
          <h3 className="mb-3 text-sm font-medium text-zinc-700 dark:text-zinc-300">
            Saved Presets
          </h3>

          {presets.length === 0 ? (
            <p data-testid="preset-empty" className="py-4 text-center text-sm text-zinc-500 dark:text-zinc-400">
              No saved presets
            </p>
          ) : (
            <ul data-testid="preset-list" className="space-y-2">
              {presets.map((preset) => (
                <li
                  key={preset.id}
                  data-testid={`preset-row-${preset.name.replace(/\s+/g, '-')}`}
                  className="flex items-center justify-between rounded-lg border border-zinc-200 px-3 py-2 dark:border-zinc-700"
                >
                  <span className="text-sm font-medium text-zinc-900 dark:text-zinc-100">
                    {preset.name}
                  </span>
                  <div className="flex items-center gap-2">
                    <Button
                      data-testid="preset-load"
                      variant="secondary"
                      onClick={() => handleLoad(preset)}
                    >
                      Load
                    </Button>
                    <Button
                      data-testid="preset-delete"
                      variant="secondary"
                      title="Delete preset"
                      disabled={deletingName === preset.name}
                      onClick={() => handleDelete(preset.name)}
                    >
                      <TrashIcon className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogBody>

      <DialogActions>
        <Button variant="ghost" onClick={onClose}>
          Close
        </Button>
      </DialogActions>
    </Dialog>
  )
}
